import { IoMdClose } from "react-icons/io";
import { DoctorTableModal } from "../../types/doctorTable/DoctorTableModal";
import { IllnessModal } from "../../types/illness/IllnessModal";
import { motion, AnimatePresence } from "framer-motion";
import defaultDoctorImage from "../../assets/images/doctors/doc.webp";
import OldPatientDetails from "./OldPatientDetails";
import DateTimeSelection from "./DateTimeSelection";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../api/store";
import { fetchAppointmentCharge } from "../../api/slices/appointmentChargeSlice";
import {
  FaCalendarAlt,
  FaClinicMedical,
  FaLock,
  FaMoneyBillWave,
  FaUserCircle,
  FaUserMd,
} from "react-icons/fa";
import {
  createOrUpdatePatient,
  PatientProfile,
} from "../../api/slices/patientProfileSlice";
import { saveAppointmentDB } from "../../api/slices/appointmentSlice";

export interface PatientData {
  id: number;
  name: string;
  gender: string;
  whatsapp: string;
  contact: string;
  nic: string;
  passport: string;
  country: string;
}

interface TimeSlotData {
  date: string;
  time: string;
  timeSlotId: number;
  bookingType: number;
}

const ConfirmationPopup: React.FC<{
  illness: IllnessModal;
  doctor: DoctorTableModal;
  onClose: () => void;
}> = ({ illness, doctor, onClose }) => {
  const initialFormData: PatientData = {
    id: 0,
    name: "",
    gender: "",
    whatsapp: "",
    contact: "",
    nic: "",
    passport: "",
    country: "Sri Lanka",
  };
  const initialTimeSlot: TimeSlotData = {
    bookingType: 0,
    date: "",
    time: "",
    timeSlotId: 0,
  };
  const dispatch = useDispatch<AppDispatch>();
  const { appointmentCharge } = useSelector(
    (state: RootState) => state.appointmentCharge
  );
  const [formData, setFormData] = useState<PatientData>(initialFormData);
  const [dateTime, setDateTime] = useState<TimeSlotData>(initialTimeSlot);
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [showDateTime, setShowDateTime] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState<{
    date: string;
    time: string;
  } | null>(null);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [paymentEmail, setPaymentEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirmClick = () => {
    setShowPatientForm(true);
  };

  const handlePatientFormSubmit = () => {
    setShowPatientForm(false);
    setShowDateTime(true);
  };

  const handleDateTimeSelect = (
    date: string,
    time: string,
    timeSlotId: number,
    bookingType: number
  ) => {
    setAppointmentDetails({ date, time });
    setDateTime({
      bookingType: bookingType,
      date: date,
      time: time,
      timeSlotId: timeSlotId,
    });
    setShowDateTime(false);
  };

  const handlePayClick = () => {
    setShowEmailPopup(true);
  };

  const handleEmailSubmit = () => {
    if (!paymentEmail || !/\S+@\S+\.\S+/.test(paymentEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setShowEmailPopup(false);
    saveAppointment();
  };

  const saveAppointment = async () => {
    setIsLoading(true);
    try {
      // Validate all required form fields
      const requiredFields = {
        name: formData.name,
        gender: formData.gender,
        whatsapp: formData.whatsapp,
        contact: formData.contact,
        country: formData.country,
        appointmentCharge: appointmentCharge?.id,
      };

      // Check for empty required fields
      const missingFields = Object.entries(requiredFields)
        .filter(([_, value]) => !value)
        .map(([key]) => key);

      if (missingFields.length > 0) {
        toast.error(
          `Please fill in all required fields: ${missingFields.join(", ")}` ||
            "An error occurred"
        );
        return;
      }

      const patientProfile: PatientProfile = {
        added_by_system_user: 0,
        address_line_1: "",
        address_line_2: "",
        address_line_3: "",
        birthday: "",
        blood_type: "",
        ch_patient_id: "",
        contact_no: formData.contact,
        continue_: 1,
        country: formData.country,
        gender: formData.gender,
        height: 0,
        name: formData.name,
        nic: formData.nic || "",
        passport: formData.passport || "",
        patient_login_id: null,
        status: 1,
        weight: 0,
        whatsapp_no: formData.whatsapp,
        createdAt: "",
        updatedAt: "",
        id: formData.id,
      };

      // Save patient profile
      const resultAction = await dispatch(
        createOrUpdatePatient(patientProfile)
      );

      if (!createOrUpdatePatient.fulfilled.match(resultAction)) {
        throw new Error("Failed to save patient profile");
      }

      const updatedProfile = resultAction.payload;

      // Calculate total payment
      const totalPayment =
        appointmentCharge.doctor_charge + appointmentCharge.hospital_charge;

      // Save appointment
      const appointmentResult = await dispatch(
        saveAppointmentDB({
          doctor_timesloat_id: dateTime.timeSlotId,
          illness_id: illness.id,
          patient_profile_id: updatedProfile.id,
          is_gift: 0,
          is_voucher: 0,
          appointment_charge_id: appointmentCharge?.id,
          is_feature: 0,
          card_payment: totalPayment + "",
          discount: 0,
          email: paymentEmail,
        })
      );

      if (saveAppointmentDB.fulfilled.match(appointmentResult)) {
        if (!appointmentResult.payload?.gateway?.redirect_url) {
          throw new Error("No payment gateway URL received");
        }
        window.location.href = appointmentResult.payload.gateway.redirect_url;
      } else {
        throw new Error("Failed to save appointment");
      }
    } catch (error: any) {
      toast.error(
        `Error: ${
          error.message ||
          "There was an error processing your appointment. Please try again."
        }`
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dateTime.bookingType && doctor?.id) {
      if (formData.country === "Sri Lanka" || formData.country === "") {
        dispatch(
          fetchAppointmentCharge({
            doctor_id: doctor?.id,
            appointment_type_id: dateTime?.bookingType,
            is_local: 1,
          })
        );
      } else {
        dispatch(
          fetchAppointmentCharge({
            doctor_id: doctor?.id,
            appointment_type_id: dateTime?.bookingType,
            is_local: 0,
          })
        );
      }
    }
  }, [dispatch, formData.country, dateTime?.bookingType, doctor?.id]);
useEffect(() => {
    // Prevent scrolling when popup is open
    document.body.style.overflow = 'hidden';
    
    // Re-enable scrolling when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex p-4 items-center justify-center z-50"
      >
        {/* Summary View */}
        {!showPatientForm && !showDateTime && (
          <motion.div
            key="summary"
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white rounded-xl  w-full max-w-md max-h-[80vh] flex flex-col"
          >
            {/* Enhanced Header */}
            <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-green-500 to-green-600 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Appointment Summary
                  </h2>
                  <p className="text-xs text-green-100">
                    Review your booking details
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
              >
                <IoMdClose size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4">
              {/* Illness Section */}
              <motion.div
                whileHover={{ y: -2 }}
                className="p-4 bg-green-50 rounded-lg border border-green-100 mb-4"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FaClinicMedical className="text-green-600 text-lg" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-800">
                      Medical Condition
                    </h4>
                    <p className="text-gray-700">{illness.illness}</p>
                  </div>
                </div>
              </motion.div>

              {/* Doctor Section */}
              <motion.div
                whileHover={{ y: -2 }}
                className="p-4 bg-green-50 rounded-lg border border-green-100 mb-4"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FaUserMd className="text-green-600 text-lg" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-green-800">Doctor</h4>
                    <div className="flex items-center space-x-3 mt-2">
                      <img
                        src={defaultDoctorImage}
                        alt={doctor.name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                      />
                      <div>
                        <p className="font-medium text-gray-800">
                          {doctor.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {doctor.specialities
                            .map((s) => s.speciality)
                            .join(", ")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Charges Section */}
              {appointmentCharge.id != 0 && (
                <motion.div
                  whileHover={{ y: -2 }}
                  className="p-4 bg-yellow-50 rounded-lg border border-yellow-100 mb-4"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <FaMoneyBillWave className="text-yellow-600 text-lg" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-yellow-800">
                        Payment Summary
                      </h4>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div>
                          <p className="text-xs text-gray-500">Doctor Fee</p>
                          <p className="text-sm font-medium text-gray-800">
                            LKR {appointmentCharge.doctor_charge.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Hospital Fee</p>
                          <p className="text-sm font-medium text-gray-800">
                            LKR {appointmentCharge.hospital_charge.toFixed(2)}
                          </p>
                        </div>
                        <div className="col-span-2 pt-2 border-t border-yellow-200 mt-1">
                          <p className="text-xs text-gray-500">Total Amount</p>
                          <p className="text-lg font-bold text-yellow-600">
                            LKR{" "}
                            {(
                              appointmentCharge.doctor_charge +
                              appointmentCharge.hospital_charge
                            ).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Date/Time Section */}
              {appointmentDetails && (
                <motion.div
                  whileHover={{ y: -2 }}
                  className="p-4 bg-purple-50 rounded-lg border border-purple-100 mb-4"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <FaCalendarAlt className="text-purple-600 text-lg" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-purple-800">
                        Appointment Time
                      </h4>
                      <div className="mt-2">
                        <p className="text-sm text-gray-700">
                          {new Date(appointmentDetails.date).toLocaleDateString(
                            "en-US",
                            {
                              weekday: "long",
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            }
                          )}
                        </p>
                        <p className="text-sm font-medium text-gray-800">
                          At {appointmentDetails.time}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Patient Details Section */}
              {formData.name && (
                <motion.div
                  whileHover={{ y: -2 }}
                  className="p-4 bg-green-50 rounded-lg border border-green-100"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <FaUserCircle className="text-green-600 text-lg" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-800">
                        Patient Details
                      </h4>
                      <div className="grid grid-cols-2 gap-3 mt-2 text-sm">
                        <div>
                          <p className="text-xs text-gray-500">Name</p>
                          <p className="text-sm font-medium text-gray-800">
                            {formData.name}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Gender</p>
                          <p className="text-sm font-medium text-gray-800">
                            {formData.gender}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Contact</p>
                          <p className="text-sm font-medium text-gray-800">
                            {formData.contact}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">WhatsApp</p>
                          <p className="text-sm font-medium text-gray-800">
                            {formData.whatsapp}
                          </p>
                        </div>
                        {formData.nic && (
                          <div>
                            <p className="text-xs text-gray-500">NIC</p>
                            <p className="text-sm font-medium text-gray-800">
                              {formData.nic}
                            </p>
                          </div>
                        )}
                        {formData.passport && (
                          <div>
                            <p className="text-xs text-gray-500">Passport</p>
                            <p className="text-sm font-medium text-gray-800">
                              {formData.passport}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sticky Action Buttons */}
            <div className="sticky bottom-0 bg-white pt-4 pb-2 border-t border-gray-200 mx-2 px-6">
              <div className="flex  space-x-3  justify-center">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all"
                >
                  Cancel
                </motion.button>
                {!appointmentDetails ? (
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleConfirmClick}
                    className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all"
                  >
                    {formData.name
                      ? "Select Date & Time"
                      : "Confirm Appointment"}
                  </motion.button>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handlePayClick}
                    className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all flex items-center"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaLock className="mr-2" />
                        Pay & Book Now
                      </>
                    )}
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Patient Details Form */}
        {showPatientForm && (
          <OldPatientDetails
            formData={formData}
            setFormData={setFormData}
            onClose={() => setShowPatientForm(false)}
            onSubmit={handlePatientFormSubmit}
          />
        )}

        {/* Date/Time Selection */}
        {showDateTime && (
          <DateTimeSelection
            doctorId={doctor.id}
            bookingType={1}
            onBack={() => {
              setShowDateTime(false);
              setFormData(initialFormData);
              setShowPatientForm(true);
            }}
            onSelectionComplete={handleDateTimeSelect}
            isNewPatient = {formData.id ? false:true}
          />
        )}

        {/* Email Popup Modal */}
        {showEmailPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-xl p-6 w-full max-w-md"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">
                  Payment Receipt Email
                </h3>
                <button
                  onClick={() => setShowEmailPopup(false)}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded-full p-1"
                >
                  <IoMdClose size={24} />
                </button>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={paymentEmail}
                  onChange={(e) => setPaymentEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter your email for payment receipt"
                />
                <p className="mt-1 text-sm text-gray-500">
                  We'll send the payment confirmation to this email
                </p>
              </div>

              {/* Payment Redirection Notice */}
              <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-100">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg
                      className="h-5 w-5 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Secure Payment Processing
                    </h3>
                    <div className="mt-1 text-sm text-blue-700">
                      <p>
                        After confirmation, you will be securely redirected to
                        OnePay, payment gateway, to complete your transaction.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowEmailPopup(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEmailSubmit}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaLock className="mr-2" />
                      Proceed to Payment
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default ConfirmationPopup;
